<template>
  <q-dialog
    v-model="shown"
    data-test-id="setup-intent-dialog"
  >
    <q-card style="width: 400px;">
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          <slot name="title">
            {{ title }}
          </slot>
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <div
          class="q-mb-lg q-mt-md"
        >
          <q-list
            class="bottom-square"
            :class="{
              'bg-responsive-grey-1': $q.dark.isActive && !stripeCardElementOnEditing && !stripeCardElementOnChange,
              'bg-white': !$q.dark.isActive || stripeCardElementOnEditing ,
              'bg-responsive-grey-9': $q.dark.isActive && stripeCardElementOnChange
            }"
            :style="{ border: stripeCardElementOnEditing ? '3px solid rgba(0, 122, 255, 0.5)' : undefined }"
            bordered
          >
            <q-item>
              <q-item-section>
                <div
                  id="setup-intent-card-element"
                  class="q-py-xs"
                />
                <div v-if="cardErrors">
                  <div
                    class="text-red q-mt-sm"
                  >
                    <q-icon
                      name="error"
                    />
                    {{ cardErrors }}
                  </div>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
          <q-btn
            color="primary"
            :text-color="$isColorDark(currentSite.primaryColor) ? 'white' : 'black'"
            size="md"
            class="full-width square-top"
            :disable="loading || disablePayWithNewCard || loadingIntent"
            :loading="loading"
            data-test-id="setup-intent-dialog-submit-button"
            @click="setupIntent"
          >
            {{ title }}
          </q-btn>

          <div
            v-if="($register || $kiosk) && $payments.status.currentReader"
            class="q-mt-xl q-mb-xs"
          >
            <q-btn
              v-if="!$payments.status.connected"
              color="secondary"
              size="md"
              class="q-py-sm full-width"
              :loading="connectingToReader"
              outline
              @click="$emit('reader-reconnect')"
            >
              {{ $t('payment.actions.reconnectToChipReader') }}
            </q-btn>
            <q-btn
              v-else
              color="secondary"
              size="md"
              class="q-py-sm q-py-xs full-width"
              :disable="true"
              :loading="$payments.status.processingPayment"
            >
              {{ $t('payment.actions.addWithChipReader') }}
            </q-btn>
            <div
              class="col q-mt-xs text-subtitle2 text-grey text-center"
            >
              <div v-if="!$payments.status.currentReader">
                {{ $t('payment.errors.noChipReaderSelected') }}
              </div>
              <div v-else-if="!$payments.status.connected">
                {{ $t('payment.errors.chipReaderNotConnected') }}
              </div>
              <div v-else-if="loadingTerminalIntent">
                {{ $t('payment.configuringReader') }}
              </div>
              <div v-else-if="$payments.status.waitingForCustomerPayment">
                {{ $t('payment.waitingForCustomerToProvidePayment') }}
              </div>
              <div v-else-if="$payments.status.processingPayment">
                {{ $t('payment.processing' ) }}
              </div>
              <div v-else-if="userOrOrder && userOrOrder.loading">
                {{ $t('creditCard.saving') }}
              </div>
              <div
                v-else-if="$payments.status.error"
                class="text-negative"
              >
                {{ $payments.status.error }}
              </div>
              <div v-else-if="$payments.status.message">
                {{ $payments.status.message }}
              </div>
              <div v-else-if="userOrOrder && userOrOrder.errors">
                {{ userOrOrder.errors[0] }}
              </div>
            </div>
          </div>
        </div>
      </q-card-section>
    </q-card>
    <action-dialog
      ref="actionDialog"
      title="Existing Pre Auth Payment"
      message="You have an existing pre auth payment. Would you like to remove it and add a new one?"
      ok-label="Add new"
      cancel-label="Keep existing"
      @ok="cancelExistingPreAuth"
      @cancel="hide"
    />
    <q-dialog
      v-model="showCardTypeDialog"
      persistent
    >
      <q-card style="width: 400px;">
        <q-card-section>
          <div class="text-h6">
            Select Card Type
          </div>
        </q-card-section>

        <q-card-section>
          <q-btn
            size="lg"
            class="row full-width q-my-md"
            align="between"
            color="blue-8"
            @click="selectCardType(SAVED_CARD_TYPES.PRE_AUTH)"
          >
            <div class="column text-left">
              <div class="col">
                Visa/Master/Discover
              </div>
              <div class="col">
                <q-icon
                  name="fa-brands fa-cc-visa"
                  class="q-mx-xs"
                />
                <q-icon
                  name="fa-brands fa-cc-mastercard"
                  class="q-mx-xs"
                />
                <q-icon
                  name="fa-brands fa-cc-discover"
                  class="q-mx-xs"
                />
              </div>
            </div>
          </q-btn>
          <q-btn
            size="lg"
            class="row full-width q-my-md"
            align="between"
            color="blue-grey-8"
            @click="selectCardType(SAVED_CARD_TYPES.SAVED)"
          >
            <div>
              Amex/Other
            </div>
            <div>
              <q-icon
                name="fa-brands fa-cc-amex"
                class="q-mx-xs"
              />
            </div>
          </q-btn>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-dialog>
</template>

<script>
import { _ } from 'core-js'
import { Notify } from 'quasar'
import { PAYMENT_STATES } from 'src/models/payment/Payment'
import ActionDialog from 'src/modules/consumer/components/ActionDialog'

export const SAVED_CARD_TYPES = {
  SAVED: 'saved',
  PRE_AUTH: 'pre-auth'
}

export default {
  name: 'SetupIntentDialog',
  configuration: ['currentSite'],
  context: ['currentRegisterClient'],
  components: {
    ActionDialog
  },
  emits: ['reader-reconnect', 'close', 'new-payment-method-added'],
  data () {
    return {
      shown: false,
      userOrOrder: null,
      loadingIntent: false,
      stripeSetupIntentClientSecret: null,
      disablePayWithNewCard: true,
      cardErrors: '',
      loading: false,
      loadingTerminalIntent: false,
      connectingToReader: false,
      stripeCardElementOnEditing: false,
      stripeCardElementOnChange: false,
      callback: null,
      lastPreAuthAttemptFailed: false,
      showCardTypeDialog: false,
      cardTypeSelected: null
    }
  },
  computed: {
    title () {
      if (!this.userOrOrder) return null

      if (this.userOrOrder.defaultPaymentMethodBrand || this.userOrOrder.savedPaymentMethodBrand) {
        return this.$i18n.t('creditCard.updateHeading')
      } else {
        return this.$i18n.t('creditCard.addHeading')
      }
    }
  },
  watch: {
    shown () {
      if (!this.shown) {
        this.callback = null
        this.cardTypeSelected = null
        this.lastPreAuthAttemptFailed = false
        this.$payments.cancelExistingTerminalCollectionAttempt()
        this.$emit('close')
      } else {
        this.cardErrors = ''
      }
    }
  },
  created () {
    _.extend(this, { SAVED_CARD_TYPES })
  },
  beforeUnmount () {
    this.$payments.cancelExistingTerminalCollectionAttempt()
  },
  methods: {
    hide () {
      this.shown = false
    },
    async configureStripe () {
      this.stripe = await this.$payments.configureStripe(this.currentSite.standardConnectAccountId)

      this.setupAddNewCard(this.stripe)

      if (!this.$station) return

      const preAuthEnabled = this.currentRegisterClient?.registerStation?.registerType?.usePreauthForOrderSavedCards
      const isOrderPayment = this.userOrOrder.typeName === 'Order'

      const shouldConfigureStripeTerminal = !preAuthEnabled ||
        !isOrderPayment ||
        this.cardTypeSelected === SAVED_CARD_TYPES.SAVED ||
        this.lastPreAuthAttemptFailed

      const hasExistingPreAuth = this.userOrOrder.preAuthPayment

      if (shouldConfigureStripeTerminal) {
        return await this.configureStripeTerminal()
      }

      if (hasExistingPreAuth) {
        this.$refs.actionDialog.show()
      } else if (this.cardTypeSelected === SAVED_CARD_TYPES.PRE_AUTH) {
        await this.getPreAuthPaymentIntent()
      } else {
        this.showCardTypeDialog = true
      }
    },
    configureStripeTerminal: async function () {
      // If we were already attempting to charge and this is a change, cancel the attempt first thing
      this.$payments.cancelExistingTerminalCollectionAttempt()

      this.loadingTerminalIntent = true

      this.terminalIntentToken = null
      await this.$payments.configureStripeTerminal()
      this.loadingTerminalIntent = false

      if (!this.$payments.status.connected) return

      this.readReusableCard()
    },
    async cancelExistingPreAuth () {
      this.loading = true
      const results = await this.userOrOrder.preAuthPayment.cancelPreAuth()

      if (results.errors?.length) {
        this.$mh.showErrors(results.errors)
        this.hide()
      } else {
        await this.userOrOrder.fetch(true)
        await this.configureStripe()
      }
      this.loading = false
    },
    async saveAndGetPaymentIntent (terminal = false, preauth = false, order = null) {
      if (!order) {
        return
      }
      order.preventOrderItemValidationsDialog = true

      // Create the payment intent
      const stripePaymentIntentClientSecret = await order.createPaymentIntent(1, terminal, preauth, errors => {
        if (errors && errors.length && _.startsWith(errors[0].description, 'Payment does not match total')) {
          let messages = [
            this.$i18n.t('pages.payment.errors.priceForItemsHaveChanged'),
            this.$i18n.t('pages.payment.confirmNewPriceAndRetryPrompt')
          ]
          const validationsToDisplay = _.difference(order.draftOrderItemValidationMessages, order.previousDraftOrderItemValidationMessages)
          order.previousDraftOrderItemValidationMessages = order.draftOrderItemValidationMessages
          if (validationsToDisplay.length) {
            messages = validationsToDisplay
          }
          this.$q.dialog({
            title: this.$i18n.t('pages.payment.priceChangeHeading'),
            message: messages.join('<br><br>'),
            html: true
          })
          order.configuration.validate()
          return
        }

        this.$mh.showErrors(errors)
      })

      if (!stripePaymentIntentClientSecret) {
        return null
      }

      return stripePaymentIntentClientSecret
    },
    async getPreAuthPaymentIntent () {
      await this.$payments.cancelExistingTerminalCollectionAttempt()

      await this.$payments.configureStripeTerminal()

      this.loading = true
      const terminalIntentToken = await this.saveAndGetPaymentIntent(true, true, this.userOrOrder)
      this.loading = false
      const { collectResult, error } = await this.$payments.collectTerminalPaymentMethod(terminalIntentToken, true, this.$mobileRegister())

      const transactionCancelRequested = error?.message === 'Transaction is cancelled by the user.'
      if (transactionCancelRequested) {
        this.loading = false
        this.shown = false
        return
      }

      if (error) {
        Notify.create({
          message: 'Collect payment error ' + error.message,
          type: 'negative',
          position: 'top'
        })
        this.loading = false
        this.shown = false
        return
      }

      const paymentResult = await this.$payments.processTerminalPayment(collectResult)
      const paymentIntent = collectResult.paymentIntent

      const payment = {
        type: 'card',
        subtype: 'present',
        stripePaymentIntentId: paymentIntent.id,
        isSavingPreAuth: true,
        amount: paymentIntent.amount / 100.00
      }

      if (paymentResult.success) {
        this.userOrOrder.payments.add(payment)
        const saveResult = await this.userOrOrder.save()

        const orderPayment = _.find(this.userOrOrder.payments.data, { stripePaymentIntentId: paymentIntent.id })

        if (saveResult.errors?.length) {
          this.$mh.showErrors(saveResult.errors)
        } else if (!orderPayment) {
          this.$mh.showErrors(['Failed to find payment'])
        } else if (orderPayment.state !== PAYMENT_STATES.PRE_AUTH) {
          const failureMessage = orderPayment.stripeFailureMessage

          this.$q.dialog({
            title: 'Pre Auth Setup Failed',
            message: failureMessage,
            ok: {
              label: 'Try Again',
              color: 'primary',
              autofocus: true
            },
            cancel: {
              label: 'Save Without Pre Auth',
              color: 'secondary'
            },
            focus: 'cancel'
          }).onOk(() => {
            this.configureStripe()
          }).onCancel(() => {
            this.lastPreAuthAttemptFailed = true
            this.configureStripe()
          })
        } else {
          this.shown = false
          this.$emit('new-payment-method-added')
        }
      } else if (paymentResult.error) {
        Notify.create({
          message: paymentResult.error,
          type: 'negative',
          position: 'top'
        })
      }
    },
    async readReusableCard () {
      const setupIntent = await this.userOrOrder.createSetupIntent(true)

      const result = await this.$payments.readReusableCard(setupIntent, this.$mobileRegister())

      const paymentMethodId = result?.collectResult?.setupIntent?.latest_attempt?.payment_method_details?.card_present?.generated_card
      // Save card to user
      if (paymentMethodId) {
        this.loading = true
        await this.userOrOrder.attachPaymentMethod(paymentMethodId)
        this.loading = false
        this.shown = false
        this.$emit('new-payment-method-added')
      } else if (result?.error?.code === 'canceled' || result?.error?.message === 'Transaction is cancelled by the user.' || !this.shown) {
        this.loading = false
        this.shown = false
        // Don't try again
      } else if (result?.error) {
        // Try again
        setTimeout(() => {
          this.readReusableCard()
        }, 500)
      } else {
        Notify.create({
          message: 'Unexpected error reading card',
          type: 'negative',
          position: 'top'
        })
      }
    },
    async connectToReader () {
      this.connectingToReader = true
      await this.$payments.connectToReader()
      this.connectingToReader = false
    },
    selectCardType (type) {
      this.cardTypeSelected = type
      this.showCardTypeDialog = false
      this.configureStripe()
    },
    setupAddNewCard (stripe) {
      const elements = stripe.elements()

      this.card = elements.create('card')

      this.card.mount('#setup-intent-card-element')

      this.card.on('change', (event) => {
        this.stripeCardElementOnChange = true
        this.disablePayWithNewCard = !!event.empty || !!event.error || !event.complete
        this.payWithNewCardErrors = event.error ? event.error.message : ''
      })
      this.card.on('focus', (event) => {
        this.stripeCardElementOnEditing = true
      })
      this.card.on('blur', (event) => {
        this.stripeCardElementOnEditing = false
      })
    },
    async show (userOrOrder, callback) {
      this.userOrOrder = userOrOrder

      this.shown = true
      this.callback = callback

      await this.$nextTick()

      await this.configureStripe()
    },
    async setupIntent () {
      if (this.disablePayWithNewCard) {
        return
      }

      const errorHandler = (errors) => {
        const formattedError = Array.isArray(errors) ? errors.join(', ') : errors
        this.cardErrors = formattedError
      }

      this.loadingIntent = true
      this.stripeSetupIntentClientSecret = await this.userOrOrder.createSetupIntent(false, errorHandler)
      this.loadingIntent = false

      if (!this.stripeSetupIntentClientSecret) {
        // Errors are already shown through errorHandler
        return
      }

      this.loading = true
      this.cardErrors = ''

      try {
        this.stripe.confirmCardSetup(
          this.stripeSetupIntentClientSecret,
          {
            payment_method: {
              card: this.card
            }
          }
        ).then(async (result) => {
          if (result.error) {
            this.$mh.showErrors([result.error.message])
            this.loading = false
          } else {
            if (this.userOrOrder.updateDefaultPaymentMethodFromStripe) {
              await this.userOrOrder.updateDefaultPaymentMethodFromStripe()
            }
            if (this.userOrOrder.updateSavedPaymentMethodFromStripe) {
              await this.userOrOrder.updateSavedPaymentMethodFromStripe()
            }
            this.loading = false
            this.$emit('new-payment-method-added')
            if (this.callback) {
              this.callback(true)
              this.callback = null
            }
            this.shown = false
          }
        })
      } catch (exception) {
        this.loading = false
        this.cardErrors = 'There was an unexpected error preparing to receive credit card'
        this.$rollbar.error('Failed to setup Stripe intent in SetupIntentDialog due to missing stripeSetupIntentClientSecret', exception)
      }
    }
  }
}
</script>
